import * as React from "react";
import {EvChargingSimilarMeterPoint} from "../../api/EvChargingApi";
import Checkbox from "../../hiberUiKit/src/atoms/Checkbox/Checkbox";
import {differingPositions, formatMprn} from "./evChargingBill";
import {evColors, evMessageStyles} from "./evChargingStyles";

interface Props {
	/**
	 * The MPRN as typed, digits only.
	 */
	digits: string;
	similarMeterPoints: EvChargingSimilarMeterPoint[];
	confirmed: boolean;
	onUseExisting: (mprn: string) => void;
	onConfirmedChange: (confirmed: boolean) => void;
}

/**
 * The guard against a typo turning into a second meter: a wrong number does not fail, it quietly splits the brand's
 * energy across two rows of the NORA file. Kept to one note under the field. When the brand has a meter one slip
 * away, both numbers are lined up with the differing digit marked, and either answer is one click.
 */
export default class NewMeterConfirm extends React.PureComponent<Props> {
	render() {
		const closest = this.props.similarMeterPoints[0];
		return closest ? this.renderLikelyTypo(closest) : this.renderNewMeter();
	}

	private renderNewMeter() {
		return (
			<div style={evMessageStyles.warning}>
				<div style={{marginBottom: 4}}>
					<strong>New meter for this partner brand.</strong> Check the number against the bill — a mistyped one would
					create a second meter.
				</div>
				<Checkbox checked={this.props.confirmed} onChange={this.props.onConfirmedChange}
						  label={"I checked the bill — it is a new meter"}/>
			</div>
		);
	}

	private renderLikelyTypo(closest: EvChargingSimilarMeterPoint) {
		const {digits} = this.props;
		return (
			<div style={evMessageStyles.warning}>
				<div><strong>Is this a typo?</strong> This partner brand already has a meter one digit away.</div>
				<table style={styles.compare}>
					<tbody>
					<tr>
						<td style={styles.label}>Typed</td>
						<td>{this.renderMarked(digits, closest.mprn)}</td>
					</tr>
					<tr>
						<td style={styles.label}>Existing</td>
						<td>
							{this.renderMarked(closest.mprn, digits)}
							{closest.nickname && <span style={{color: evColors.muted}}> · {closest.nickname}</span>}
						</td>
					</tr>
					</tbody>
				</table>
				<div style={styles.answers}>
					<button type="button" style={styles.useExisting} onClick={this.useExisting}>
						Use {formatMprn(closest.mprn)}
					</button>
					<Checkbox checked={this.props.confirmed} onChange={this.props.onConfirmedChange}
							  label={`No typo — ${formatMprn(digits)} is a new meter`}/>
				</div>
			</div>
		);
	}

	/**
	 * The MPRN grouped 4-3-4, with the digits that differ from the other one marked.
	 */
	private renderMarked(mprn: string, other: string) {
		const changed = differingPositions(mprn, other);
		let position = 0;
		return (
			<strong style={styles.number}>
				{formatMprn(mprn).split("").map((char, i) => {
					if (char === " ") return <span key={i}> </span>;
					const differs = changed.indexOf(position++) >= 0;
					return <span key={i} style={differs ? styles.changed : undefined}>{char}</span>;
				})}
			</strong>
		);
	}

	private useExisting = () => {
		const closest = this.props.similarMeterPoints[0];
		if (closest) this.props.onUseExisting(closest.mprn);
	};
}

const styles: { [key: string]: React.CSSProperties } = {
	compare: {margin: "6px 0", borderCollapse: "collapse", fontSize: 13, lineHeight: "22px"},
	label: {color: evColors.muted, paddingRight: 12, fontSize: 12},
	number: {letterSpacing: ".06em", fontVariantNumeric: "tabular-nums"},
	changed: {background: evColors.error, color: "#fff", borderRadius: 2, padding: "0 2px"},
	answers: {display: "flex", alignItems: "center", flexWrap: "wrap", gap: 16},
	useExisting: {
		border: `1px solid ${evColors.navy}`, background: "#fff", color: evColors.navy, borderRadius: 4,
		padding: "3px 10px", fontSize: 12, lineHeight: "17px", cursor: "pointer"
	}
};
